import { FunctionDeclaration, Schema, Tool } from "@google/genai";

export interface User {
  id: string;
  username: string;
  email?: string;
  avatarUrl?: string;
}

export interface Attachment {
  id: string;
  name: string;
  type: 'image' | 'document';
  data?: string; // Base64 string or text content (optional if stored on Drive)
  mimeType: string;
  timestamp: number;
  driveFileId?: string; // Google Drive file ID
  driveWebLink?: string; // Shareable web link to the file
  sectionId?: string; // Optional: Associate with a specific section
}

export interface Section {
  id: string;
  title: string;
  content: string; // HTML Content
  lastUpdated: number;
  parentId?: string | null; // For hierarchy: null = Root Section
  order: number; // For drag and drop ordering
  conflictBadge?: boolean; // Shows conflict indicator in UI
  conflictOriginalId?: string; // For "Keep Both" - links to original section
}

export interface Idea {
  id: string;
  title: string;
  content: string; // Fallback for legacy / simple summary
  sections?: Section[]; // New Subcategories
  attachments?: Attachment[]; // New Attachments
  plan?: ExecutionPlan | null; // Stored execution plan for this idea
  googleDocId?: string; // ID of the synchronized Google Doc (project content)
  googleChatDocId?: string; // ID of the synchronized Chat History Doc
  googleFolderId?: string; // ID of the project folder in Drive
  createdAt: number;
  updatedAt: number;
  accountId?: string; // Account namespace: 'guest' or Google account ID
}

export enum MessageRole {
  USER = 'user',
  MODEL = 'model',
  SYSTEM = 'system'
}

export interface GroundingSource {
  uri: string;
  title: string;
}

export interface ProposalChange {
  id: string;
  action: 'create_section' | 'update_section' | 'delete_section' | 'update_concept_title' | 'update_concept_overview';
  sectionId?: string; // For updates/deletes/creation (if creating subsection)
  parentId?: string; // For creating new sections
  title?: string;
  content?: string;
  reasoning: string;
  status: 'pending' | 'accepted' | 'rejected' | 'revision_requested';
}

export interface PendingProposal {
  id: string;
  type: 'batch_update' | 'create_concept';
  title?: string; // For concept creation
  changes: ProposalChange[]; // List of granular changes
  originalMessageId: string;
  status: 'active' | 'completed'; // Aggregate status
}

export interface PlanStep {
  id: string;
  description: string;
  status: 'pending' | 'in_progress' | 'completed' | 'failed';
}

export interface ExecutionPlan {
  title: string;
  steps: PlanStep[];
  isActive: boolean;
}

export interface SuggestedAction {
  label: string;
  message: string;
}

export interface Message {
  id: string;
  role: MessageRole;
  text: string;
  timestamp: number;
  ideaId?: string | null; // null means "episodic/global"
  groundingSources?: GroundingSource[];
  proposal?: PendingProposal; // If the message contains a proposal
  isLoading?: boolean;
  isPlanUpdate?: boolean; // Identifies if this message updated the execution plan
  suggestedActions?: SuggestedAction[]; // Quick reply buttons
  isBriefing?: boolean; // Briefing from delegated search (voice -> text)
  briefingQuery?: string; // The original query that triggered the briefing
  fallbackModel?: string; // If a fallback model was used due to server overload
  accountId?: string; // Account namespace: 'guest' or Google account ID
  usage?: {
    promptTokens: number;
    completionTokens: number;
    totalTokens: number;
  };
}

export interface ChatState {
  messages: Message[];
}

export interface LiveConfig {
  model: string;
  voiceName: string;
}

// --- File System Access API Types ---
export interface FileSystemHandle {
  kind: 'file' | 'directory';
  name: string;
  isSameEntry: (other: FileSystemHandle) => Promise<boolean>;
}

export interface FileSystemFileHandle extends FileSystemHandle {
  kind: 'file';
  getFile: () => Promise<File>;
  createWritable: () => Promise<FileSystemWritableFileStream>;
}

export interface FileSystemDirectoryHandle extends FileSystemHandle {
  kind: 'directory';
  getFileHandle: (name: string, options?: { create?: boolean }) => Promise<FileSystemFileHandle>;
  getDirectoryHandle: (name: string, options?: { create?: boolean }) => Promise<FileSystemDirectoryHandle>;
  removeEntry: (name: string, options?: { recursive?: boolean }) => Promise<void>;
  resolve: (possibleDescendant: FileSystemHandle) => Promise<string[] | null>;
  values: () => AsyncIterableIterator<FileSystemHandle>;
}

export interface FileSystemWritableFileStream extends WritableStream {
  write: (data: any) => Promise<void>;
  seek: (position: number) => Promise<void>;
  truncate: (size: number) => Promise<void>;
}